/**
 * Order / money / rating columns shared by the doctors, patients and services reports.
 */
import { Box, Chip, Typography } from '@mui/material';
import type { TFunction } from 'i18next';
import moment from 'moment';
import type { ReportColumn } from './DetailedReportTable';
import type { ReportMetrics } from '../../store/api/detailedReportsApi';
import { formatCurrency } from '../../utils/format';

const ratingText = (value: number | null) => (value === null ? null : Number(value.toFixed(1)));

export function metricColumns<Row extends ReportMetrics>(
  t: TFunction,
  options: { providerRating?: boolean } = {},
): ReportColumn<Row>[] {
  const columns: ReportColumn<Row>[] = [
    {
      key: 'total_orders',
      label: t('reports.detailed.cols.totalOrders'),
      align: 'center',
      value: (r) => r.total_orders,
      render: (r) => <Typography fontWeight={700}>{r.total_orders}</Typography>,
    },
    { key: 'completed_orders', label: t('reports.detailed.cols.completed'), align: 'center', value: (r) => r.completed_orders },
    {
      key: 'active_orders',
      label: t('reports.detailed.cols.open'),
      align: 'center',
      value: (r) => r.pending_orders + r.active_orders,
    },
    { key: 'cancelled_orders', label: t('reports.detailed.cols.cancelled'), align: 'center', value: (r) => r.cancelled_orders },
    {
      key: 'completion_rate',
      label: t('reports.detailed.cols.completionRate'),
      align: 'center',
      value: (r) => r.completion_rate,
      render: (r) => (r.completion_rate === null ? '—' : `${r.completion_rate}%`),
    },
    {
      key: 'completed_value',
      label: t('reports.detailed.cols.completedValue'),
      align: 'right',
      value: (r) => r.completed_value,
      render: (r) => <Typography fontWeight={600}>{formatCurrency(r.completed_value)}</Typography>,
    },
    {
      key: 'open_value',
      label: t('reports.detailed.cols.openValue'),
      align: 'right',
      value: (r) => r.open_value,
      render: (r) => formatCurrency(r.open_value),
    },
    {
      key: 'avg_service_rating',
      label: t('reports.detailed.cols.serviceRating'),
      align: 'center',
      value: (r) => ratingText(r.avg_service_rating),
      render: (r) =>
        r.avg_service_rating === null ? (
          '—'
        ) : (
          <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
            ★ {r.avg_service_rating.toFixed(1)}{' '}
            <Typography component="span" variant="caption" color="text.secondary">
              ({r.ratings_count})
            </Typography>
          </Box>
        ),
    },
  ];
  if (options.providerRating) {
    columns.push({
      key: 'avg_provider_rating',
      label: t('reports.detailed.cols.providerRating'),
      align: 'center',
      value: (r) => ratingText(r.avg_provider_rating),
      render: (r) => (r.avg_provider_rating === null ? '—' : `★ ${r.avg_provider_rating.toFixed(1)}`),
    });
  }
  columns.push({
    key: 'last_order_at',
    label: t('reports.detailed.cols.lastOrder'),
    value: (r) => (r.last_order_at ? moment(r.last_order_at).format('YYYY-MM-DD') : null),
  });
  return columns;
}

export const activeChip = (t: TFunction, active: boolean) =>
  active ? null : <Chip size="small" label={t('reports.detailed.inactive')} sx={{ ml: 1 }} />;
